/**
 * API для ручных рассылок (Manual Broadcasts)
 * Дата: 26.02.2026 — PLAN Manual Broadcasts, Итерация 5
 * Связан с: internal/handler/manual_broadcast.go
 */
import { apiClient } from './client';
import type { WABAPreview } from './templates';

export interface ManualBroadcast {
  id: string;
  tenant_id: string;
  name: string;
  segment_type: 'regular' | 'dynamic' | 'lk';
  segment_id: number;
  segment_name: string;
  template_id: string;
  template_name: string;
  channel_override: string;
  launch_origin?: 'manually' | 'cron';
  variable_mapping?: Record<string, { type: string; field?: string; value?: string }>;
  selected_phones?: string[];
  launch_locked?: boolean;
  scheduled_at?: string;
  stagger_minutes: number;
  ttl_minutes: number;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  skipped_count: number;
  status: string;
  started_at?: string;
  completed_at?: string;
  error?: string;
  created_by_id: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

export interface SegmentInfo {
  id: number;
  name: string;
  description?: string;
  count?: number;
  patients_count?: number;
  created_at?: string;
}

export interface DynamicSegmentInfo {
  id: number;
  name: string;
  description?: string;
  patients_count?: number;
  created_at?: string;
}

export interface LkSegmentInfo {
  id: number;
  name: string;
  description?: string;
  patients_count?: number;
  created_at?: string;
}

export interface SegmentPatientPreview {
  patients: { phone: string; name: string; variables?: Record<string, string> }[];
  total: number;
  page: number;
  per_page: number;
}

export interface ManualBroadcastListResponse {
  broadcasts: ManualBroadcast[];
  total: number;
}

export interface SegmentPatient {
  phone: string;
  name: string;
  variables?: Record<string, string>;
}

export interface LatestPatientManualHistory {
  lookup_phone: string;
  phone: string;
  phone_normalized?: string;
  broadcast_id: string;
  broadcast_name: string;
  template_name: string;
  channel?: string;
  status: string;
  sent_at?: string;
  created_at: string;
}

export interface CreateBroadcastRequest {
  name: string;
  segment_type: 'regular' | 'dynamic' | 'lk';
  segment_id: number;
  segment_name: string;
  template_id: string;
  channel_override?: string;
  selected_phones?: string[];
  variable_mapping?: Record<string, { type: string; field?: string; value?: string }>;
  stagger_minutes?: number;
  ttl_minutes?: number;
}

export interface ManualBroadcastSettings {
  manual_broadcasts_enabled: boolean;
}

export interface SendManualBroadcastTestRequest extends PreviewManualBroadcastRecipientRequest {
  mode?: 'snapshot' | 'deep_emulation';
}

export interface SendManualBroadcastTestResponse {
  success: boolean;
  message: string;
  error?: string;
  target_label?: string;
  target_value?: string;
  target_channel?: string;
  applied_mode?: 'snapshot' | 'deep_emulation';
}

export const manualBroadcastsApi = {
  list: async (params?: { status?: string; segment_id?: string; limit?: number; offset?: number; tenant_id?: string }): Promise<ManualBroadcastListResponse> => {
    const q: Record<string, string> = {};
    if (params?.tenant_id) q.tenant_id = params.tenant_id;
    if (params?.status) q.status = params.status;
    if (params?.segment_id) q.segment_id = params.segment_id;
    if (params?.limit != null) q.limit = String(params.limit);
    if (params?.offset != null) q.offset = String(params.offset);
    const res = await apiClient.get<ManualBroadcastListResponse>('/manual-broadcasts', { params: q });
    return { broadcasts: res.data.broadcasts ?? [], total: res.data.total ?? 0 };
  },

  get: async (id: string, tenantId?: string): Promise<ManualBroadcast> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.get<ManualBroadcast>(`/manual-broadcasts/${id}`, { params: q });
    return res.data;
  },

  create: async (data: CreateBroadcastRequest, tenantId?: string): Promise<ManualBroadcast> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.post<ManualBroadcast>('/manual-broadcasts', data, { params: q });
    return res.data;
  },

  update: async (id: string, data: Partial<CreateBroadcastRequest>, tenantId?: string): Promise<ManualBroadcast> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.put<ManualBroadcast>(`/manual-broadcasts/${id}`, data, { params: q });
    return res.data;
  },

  delete: async (id: string, tenantId?: string): Promise<void> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    await apiClient.delete(`/manual-broadcasts/${id}`, { params: q });
  },

  launch: async (id: string, tenantId?: string): Promise<{ message: string; broadcast_id: string }> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.post<{ message: string; broadcast_id: string }>(`/manual-broadcasts/${id}/launch`, null, { params: q });
    return res.data;
  },

  cancel: async (id: string, tenantId?: string): Promise<{ message: string; broadcast_id: string }> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.post<{ message: string; broadcast_id: string }>(`/manual-broadcasts/${id}/cancel`, null, { params: q });
    return res.data;
  },

  getSettings: async (tenantId: string): Promise<ManualBroadcastSettings> => {
    const res = await apiClient.get<ManualBroadcastSettings>('/settings/manual-broadcasts', {
      params: { tenant_id: tenantId },
    });
    return res.data;
  },

  updateSettings: async (tenantId: string, manualBroadcastsEnabled: boolean): Promise<ManualBroadcastSettings> => {
    const res = await apiClient.put<ManualBroadcastSettings>(
      '/settings/manual-broadcasts',
      { manual_broadcasts_enabled: manualBroadcastsEnabled },
      { params: { tenant_id: tenantId } },
    );
    return res.data;
  },

  setLaunchLocked: async (
    ids: string[],
    launchLocked: boolean,
    tenantId?: string,
  ): Promise<{ updated: number; launch_locked: boolean; message: string }> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.post<{ updated: number; launch_locked: boolean; message: string }>(
      '/manual-broadcasts/launch-lock',
      { ids, launch_locked: launchLocked },
      { params: q },
    );
    return res.data;
  },

  listSegments: async (tenantId?: string): Promise<SegmentInfo[]> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.get<{ segments?: SegmentInfo[] } | SegmentInfo[]>('/manual-broadcasts/segments', { params: q });
    const data = res.data;
    return Array.isArray(data) ? data : (data?.segments ?? []);
  },

  getSegmentPreview: async (id: string, tenantId?: string): Promise<SegmentPatientPreview> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.get<SegmentPatientPreview>(`/manual-broadcasts/segments/${id}/preview`, { params: q });
    return res.data;
  },

  listDynamicSegments: async (tenantId?: string): Promise<DynamicSegmentInfo[]> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.get<{ segments?: DynamicSegmentInfo[] } | DynamicSegmentInfo[]>('/manual-broadcasts/dynamic-segments', { params: q });
    const data = res.data;
    return Array.isArray(data) ? data : (data?.segments ?? []);
  },

  getDynamicSegmentPreview: async (id: string, tenantId?: string): Promise<SegmentPatientPreview> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.get<SegmentPatientPreview>(`/manual-broadcasts/dynamic-segments/${id}/preview`, { params: q });
    return res.data;
  },

  listLkSegments: async (tenantId?: string): Promise<LkSegmentInfo[]> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.get<{ segments?: LkSegmentInfo[] } | LkSegmentInfo[]>('/manual-broadcasts/lk-segments', { params: q });
    const data = res.data;
    return Array.isArray(data) ? data : (data?.segments ?? []);
  },

  getLkSegmentPreview: async (id: string, tenantId?: string): Promise<SegmentPatientPreview> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.get<SegmentPatientPreview>(`/manual-broadcasts/lk-segments/${id}/preview`, { params: q });
    return res.data;
  },

  loadAllSegmentPatients: async (
    segmentType: 'regular' | 'dynamic' | 'lk',
    segmentId: number,
    tenantId?: string
  ): Promise<SegmentPatient[]> => {
    const allPatients: SegmentPatient[] = [];
    const perPage = 200;
    let page = 1;
    const endpoint = segmentType === 'dynamic'
      ? `/manual-broadcasts/dynamic-segments/${segmentId}/preview`
      : segmentType === 'lk'
        ? `/manual-broadcasts/lk-segments/${segmentId}/preview`
        : `/manual-broadcasts/segments/${segmentId}/preview`;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const q: Record<string, string> = { page: String(page), per_page: String(perPage) };
      if (tenantId) q.tenant_id = tenantId;
      const res = await apiClient.get<SegmentPatientPreview>(endpoint, { params: q });
      const patients = res.data.patients ?? [];
      allPatients.push(...patients);
      if (patients.length < perPage || allPatients.length >= res.data.total) break;
      page++;
    }
    return allPatients;
  },

  /** История отправок по пациентам — Итерация 6 */
  getPatientsHistory: async (params?: {
    tenant_id?: string;
    phone?: string;
    date_from?: string;
    date_to?: string;
    status?: string;
    broadcast_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ items: PatientHistoryEntry[]; total: number }> => {
    const q: Record<string, string> = {};
    if (params?.tenant_id) q.tenant_id = params.tenant_id;
    if (params?.phone) q.phone = params.phone;
    if (params?.date_from) q.date_from = params.date_from;
    if (params?.date_to) q.date_to = params.date_to;
    if (params?.status) q.status = params.status;
    if (params?.broadcast_id) q.broadcast_id = params.broadcast_id;
    if (params?.limit != null) q.limit = String(params.limit);
    if (params?.offset != null) q.offset = String(params.offset);
    const res = await apiClient.get<{ items: PatientHistoryEntry[]; total: number }>('/manual-broadcasts/patients-history', { params: q });
    return { items: res.data.items ?? [], total: res.data.total ?? 0 };
  },

  getUnifiedHistory: async (params?: {
    tenant_id?: string;
    view_type?: 'manual' | 'auto';
    search?: string;
    date_from?: string;
    date_to?: string;
    channel?: string;
    transport_status?: string;
    behavioral_status?: string;
    launch_origin?: string;
    source_key?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ items: BroadcastJournalEntry[]; total: number }> => {
    const q: Record<string, string> = {};
    if (params?.tenant_id) q.tenant_id = params.tenant_id;
    if (params?.view_type) q.view_type = params.view_type;
    if (params?.search) q.search = params.search;
    if (params?.date_from) q.date_from = params.date_from;
    if (params?.date_to) q.date_to = params.date_to;
    if (params?.channel) q.channel = params.channel;
    if (params?.transport_status) q.transport_status = params.transport_status;
    if (params?.behavioral_status) q.behavioral_status = params.behavioral_status;
    if (params?.launch_origin) q.launch_origin = params.launch_origin;
    if (params?.source_key) q.source_key = params.source_key;
    if (params?.limit != null) q.limit = String(params.limit);
    if (params?.offset != null) q.offset = String(params.offset);
    const res = await apiClient.get<{ items: BroadcastJournalEntry[]; total: number }>('/manual-broadcasts/unified-history', { params: q });
    return { items: res.data.items ?? [], total: res.data.total ?? 0 };
  },

  /** Последняя успешная ручная отправка по списку телефонов — 17.03.2026 */
  getPatientsLatestHistory: async (
    phones: string[],
    tenantId?: string,
  ): Promise<Record<string, LatestPatientManualHistory>> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.post<{ items: LatestPatientManualHistory[] }>(
      '/manual-broadcasts/patients-last-history',
      { phones },
      { params: q },
    );
    return Object.fromEntries((res.data.items ?? []).map((item) => [item.lookup_phone, item]));
  },

  /** Получатели рассылки — Итерация 6 */
  listRecipients: async (
    broadcastId: string,
    params?: { status?: string; search?: string; limit?: number; offset?: number; tenant_id?: string }
  ): Promise<{ recipients: BroadcastRecipient[]; total: number }> => {
    const q: Record<string, string> = {};
    if (params?.status) q.status = params.status;
    if (params?.search) q.search = params.search;
    if (params?.limit != null) q.limit = String(params.limit);
    if (params?.offset != null) q.offset = String(params.offset);
    if (params?.tenant_id) q.tenant_id = params.tenant_id;
    const res = await apiClient.get<{ recipients: BroadcastRecipient[]; total: number }>(`/manual-broadcasts/${broadcastId}/recipients`, { params: q });
    return { recipients: res.data.recipients ?? [], total: res.data.total ?? 0 };
  },

  previewRecipients: async (
    broadcastId: string,
    params?: { search?: string; page?: number; per_page?: number; tenant_id?: string }
  ): Promise<{ recipients: BroadcastRecipientPreview[]; total: number; page: number; per_page: number; is_preview: boolean }> => {
    const q: Record<string, string> = {};
    if (params?.search) q.search = params.search;
    if (params?.page != null) q.page = String(params.page);
    if (params?.per_page != null) q.per_page = String(params.per_page);
    if (params?.tenant_id) q.tenant_id = params.tenant_id;
    const res = await apiClient.get<{
      recipients: BroadcastRecipientPreview[];
      total: number;
      page: number;
      per_page: number;
      is_preview: boolean;
    }>(`/manual-broadcasts/${broadcastId}/preview-recipients`, { params: q });
    return {
      recipients: res.data.recipients ?? [],
      total: res.data.total ?? 0,
      page: res.data.page ?? 1,
      per_page: res.data.per_page ?? 50,
      is_preview: Boolean(res.data.is_preview),
    };
  },

  /** Единый preview одного получателя для create/draft flows — 17.03.2026 */
  previewRecipient: async (
    data: PreviewManualBroadcastRecipientRequest,
    tenantId?: string,
  ): Promise<BroadcastRecipientPreview> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.post<BroadcastRecipientPreview>(
      '/manual-broadcasts/preview-recipient',
      data,
      { params: q },
    );
    return res.data;
  },

  /** Контрольная отправка preview получателя — 17.03.2026 */
  sendTestRecipient: async (
    data: SendManualBroadcastTestRequest,
    tenantId?: string,
  ): Promise<SendManualBroadcastTestResponse> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.post<SendManualBroadcastTestResponse>(
      '/manual-broadcasts/send-test',
      data,
      { params: q },
    );
    return res.data;
  },

  sendRecipient: async (
    broadcastId: string,
    phone: string,
    tenantId?: string
  ): Promise<{ message: string; recipient?: BroadcastRecipient }> => {
    const q: Record<string, string> = {};
    if (tenantId) q.tenant_id = tenantId;
    const res = await apiClient.post<{ message: string; recipient?: BroadcastRecipient }>(
      `/manual-broadcasts/${broadcastId}/send-recipient`,
      { phone },
      { params: q }
    );
    return res.data;
  },
};

export interface PatientHistoryEntry {
  id: string;
  broadcast_id: string;
  phone: string;
  patient_name?: string;
  broadcast_name: string;
  template_name: string;
  channel?: string;
  status: string;
  skip_reason?: string;
  error_message?: string;
  sent_at?: string;
  created_at: string;
}

export interface BroadcastJournalEntry {
  id: string;
  view_type: 'manual' | 'auto';
  launch_origin: 'manually' | 'cron' | string;
  source_label: string;
  source_key?: string;
  source_event_type?: string;
  source_pipeline?: string;
  broadcast_group?: string;
  broadcast_id?: string;
  template_id?: string;
  template_name?: string;
  phone: string;
  patient_name?: string;
  channel?: string;
  fallback_used: boolean;
  fallback_channel?: string;
  transport_status: string;
  behavioral_status: string;
  behavioral_label: string;
  error_reason?: string;
  sent_at: string;
  delivered_at?: string;
  interaction_type?: string;
  requires_action: boolean;
  response_received: boolean;
  was_read: boolean;
}

export interface BroadcastRecipient {
  id: string;
  broadcast_id: string;
  phone: string;
  patient_name?: string;
  status: string;
  channel?: string;
  skip_reason?: string;
  error_message?: string;
  sent_at?: string;
  created_at: string;
}

export interface BroadcastRecipientPreview {
  phone: string;
  patient_name?: string;
  channel: string;
  template_type: string;
  preview_text?: string;
  resolved_variables?: Record<string, string>;
  waba_preview?: WABAPreview | null;
}

export interface PreviewManualBroadcastRecipientRequest {
  phone: string;
  broadcast_id?: string;
  segment_type?: 'regular' | 'dynamic' | 'lk';
  segment_id?: number;
  template_id?: string;
  channel_override?: string;
  variable_mapping?: Record<string, { type: string; field?: string; value?: string }>;
}
